/**
 * Terms of Service Page
 *
 * v2.1 - Session 501: price de-hardcode; source setup fee from plan catalog - 2026-06-13
 * v2.0 - 2026-03-23 - Refactored to use LegalPageShell; dark-mode-safe colors; legal@ email
 * v1.2 - 2026-03-17 - GDPR-03: Update /privacy-policy link to /legal/privacy
 * v1.1 - 2026-03-16 - MKTG-03: Replace 3x mailto:hello@mylojic.com with ContactFormModal
 * v1.0 - 2026-03-15 - GDPR-01: Terms of Service page
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Divider,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import LegalPageShell, { LEGAL_STICKY_BAR_HEIGHT, legalBodyTextSx } from '../components/LegalPageShell';
import { plansService, Tier } from '../services/api';
import { computeSetupFeeDisplay } from '../utils/planCatalog';

// ─── Section registry ─────────────────────────────────────────────────────────
const SECTIONS = [
  { id: 'acceptance',      label: '1. Acceptance of Terms' },
  { id: 'description',     label: '2. Description of Service' },
  { id: 'accounts',        label: '3. User Accounts' },
  { id: 'acceptable-use',  label: '4. Acceptable Use' },
  { id: 'payment',         label: '5. Payment Terms' },
  { id: 'ip',              label: '6. Intellectual Property' },
  { id: 'privacy',         label: '7. Data Privacy' },
  { id: 'liability',       label: '8. Disclaimers & Liability' },
  { id: 'termination',     label: '9. Termination' },
  { id: 'governing-law',   label: '10. Governing Law' },
  { id: 'contact',         label: '11. Contact' },
];

const TermsOfService: React.FC = () => {
  const [tiers, setTiers] = useState<Tier[]>([]);

  useEffect(() => {
    plansService.getPlans().then(setTiers).catch(() => {});
  }, []);

  const setupFeeDisplay = computeSetupFeeDisplay(tiers);

  return (
  <LegalPageShell title="Terms of Service" lastUpdated="March 23, 2026" sections={SECTIONS}>
    <Container maxWidth="md" sx={{ py: 6 }}>

      {/* Acceptance notice */}
      <Paper elevation={2} sx={{ p: 3, bgcolor: 'warning.light', mb: 5 }}>
        <Typography variant="h6" sx={{ color: 'warning.dark', fontWeight: 700, mb: 1 }}>
          Please Read Before Using MyLojic
        </Typography>
        <Typography sx={{ color: 'warning.dark' }}>
          By accessing or using the MyLojic platform, you agree to be bound by
          these Terms of Service. If you do not agree, do not use the service.
          These Terms apply to all users, including account owners, team members, and
          visitors.
        </Typography>
      </Paper>

      {/* Section 1 */}
      <Box id="acceptance" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
          1. Acceptance of Terms
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          These Terms of Service ("Terms") constitute a legally binding agreement
          between you and Data Edge Services LLC, a Texas limited liability
          company doing business as MyLojic ("Company," "we," "us," "our").
          These Terms govern your use of the MyLojic web application, APIs, and
          all related services (collectively, the "Service").
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          By creating an account, clicking "I Agree," or otherwise using the
          Service, you accept these Terms in full. If you are using the Service
          on behalf of a business, you represent that you have the authority to
          bind that business to these Terms.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 2 */}
      <Box id="description" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
          2. Description of Service
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          MyLojic is an AI-powered data intelligence platform designed for small
          and medium-sized businesses (SMBs). The Service connects to your
          existing business data sources (such as accounting software, CRMs, and
          sales platforms), processes and analyzes your data, and delivers
          actionable insights, KPI dashboards, and AI-generated recommendations,
          all without requiring technical expertise on your part.
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Core capabilities include: automated data discovery, schema analysis,
          guided transformation approvals, Gold-layer aggregations, KPI card
          generation, prescriptive recommendations, and natural language Q&A over
          your business data.
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          The Service is currently available to businesses located in the United
          States. International availability will be announced separately.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 3 */}
      <Box id="accounts" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
          3. User Accounts and Responsibilities
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5 }}>
          3.1 Account Registration
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          You must provide accurate, complete, and current information when
          creating your account. You are responsible for keeping your account
          information up to date.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5 }}>
          3.2 Account Security
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          You are responsible for maintaining the confidentiality of your login
          credentials. You agree to notify us immediately at{' '}
          <Link href="mailto:legal@mylojic.com" sx={{ color: 'primary.main' }}>
            legal@mylojic.com
          </Link>{' '}
          if you suspect unauthorized access to your account. We are not liable for any
          loss resulting from unauthorized use of your credentials.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5 }}>
          3.3 Team Members
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Account owners may invite team members. You are responsible for all
          actions taken by team members under your account. You must revoke
          access promptly when a team member no longer has authorization to use
          the Service.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 4 */}
      <Box id="acceptable-use" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
          4. Acceptable Use
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          You agree to use the Service only for lawful purposes and in accordance
          with these Terms. You must not:
        </Typography>
        <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3, pl: 3 }}>
          <li>Upload data you do not have the legal right to share</li>
          <li>Use the Service to engage in any illegal activity or fraud</li>
          <li>
            Attempt to reverse engineer, decompile, or extract source code from
            the Service
          </li>
          <li>
            Interfere with the Service's infrastructure or circumvent security
            measures
          </li>
          <li>
            Use the Service to build a competing product or resell access to
            third parties
          </li>
          <li>
            Upload content that is harmful, offensive, or violates third-party
            rights
          </li>
          <li>
            Misrepresent your identity or affiliation when using the Service
          </li>
        </Box>
        <Typography paragraph sx={legalBodyTextSx}>
          We reserve the right to suspend or terminate access for any violation
          of this section without prior notice.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 5 */}
      <Box id="payment" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
          5. Payment Terms
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5 }}>
          5.1 Subscription Fees
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Access to the Service is provided on a paid subscription basis. Current
          pricing is available at{' '}
          <Link href="/pricing" sx={{ color: 'primary.main' }}>www.mylojic.com/pricing</Link>.
          Subscription fees are billed monthly at{' '}
          <strong>the rate for your selected plan</strong> and are
          processed through our payment partner, Stripe.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5 }}>
          5.2 Setup Fee
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          A one-time setup fee of{' '}
          <strong>{setupFeeDisplay ?? 'the amount specified in your plan'}</strong>{' '}
          is charged upon account activation. This fee covers onboarding, initial data pipeline
          configuration, and account provisioning. Setup fees are
          non-refundable once your account has been activated and provisioned.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5 }}>
          5.3 Billing and Renewal
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Subscriptions renew automatically on a monthly basis unless cancelled
          before the renewal date. You authorize us to charge your payment
          method on file for each renewal. Monthly subscription fees are
          non-refundable for the current billing cycle once processed.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5 }}>
          5.4 Cancellation
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          You may cancel your subscription at any time through your account
          settings or by contacting us at{' '}
          <Link href="mailto:legal@mylojic.com" sx={{ color: 'primary.main' }}>
            legal@mylojic.com
          </Link>.
          Cancellation takes effect at the end of the current billing period.
          We do not provide prorated refunds for partial months.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 6 */}
      <Box id="ip" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
          6. Intellectual Property
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5 }}>
          6.1 MyLojic Owns the Platform
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          The Service, including all software, algorithms, AI models, designs,
          documentation, trademarks, and brand assets, is the exclusive
          intellectual property of Data Edge Services LLC. These Terms grant you
          a limited, non-exclusive, non-transferable right to use the Service for
          your internal business purposes. No other rights are granted.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5 }}>
          6.2 You Own Your Data
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          All data you upload, connect, or generate through the Service
          ("Business Data") remains your property. We do not claim ownership over
          your Business Data. By using the Service, you grant us a limited
          license to process your Business Data solely to provide the Service to
          you. We do not sell or share your Business Data with third parties for
          their own use.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5 }}>
          6.3 Feedback
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          If you submit feedback, suggestions, or ideas about the Service, you
          grant us a perpetual, royalty-free license to use and incorporate that
          feedback without any obligation to you.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 7 */}
      <Box id="privacy" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
          7. Data Privacy
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Your privacy is important to us. Our{' '}
          <Link href="/legal/privacy" sx={{ color: 'primary.main' }}>Privacy Policy</Link>{' '}
          explains what data we collect, how we use it, and your rights regarding your personal
          information, including your rights under the California Consumer
          Privacy Act (CCPA). By using the Service, you agree to our data
          practices as described in the Privacy Policy.
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          The Service uses cookies. You can manage cookie preferences through the
          consent banner shown on your first visit. Essential cookies are always
          active. Optional analytics cookies are only activated with your consent.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 8 */}
      <Box id="liability" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
          8. Disclaimers and Limitation of Liability
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5 }}>
          8.1 No Professional Advice
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          AI-generated insights, KPI summaries, and recommendations provided by
          the Service are for informational purposes only. They do not constitute
          financial, legal, tax, or professional business advice. You are solely
          responsible for decisions made based on information from the Service.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5 }}>
          8.2 Service Availability
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          We strive for high availability but do not guarantee uninterrupted
          access to the Service. The Service is provided "as is" and "as
          available" without warranties of any kind, express or implied.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5 }}>
          8.3 Limitation of Liability
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography sx={{ ...legalBodyTextSx, fontWeight: 500 }}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, DATA EDGE SERVICES LLC SHALL
            NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR
            PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL
            LIABILITY SHALL NOT EXCEED THE FEES YOU PAID IN THE TWELVE MONTHS
            PRECEDING THE CLAIM.
          </Typography>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 9 */}
      <Box id="termination" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
          9. Termination
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Either party may terminate these Terms at any time. You may cancel your
          account through your account settings. We may suspend or terminate your
          access if you violate these Terms, fail to pay fees, or if we are
          required to do so by law. Upon termination, your right to use the
          Service ends immediately. We will delete your Business Data within 30
          days of termination unless required to retain it by law.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 10 */}
      <Box id="governing-law" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
          10. Governing Law
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          These Terms are governed by the laws of the State of Texas, United
          States of America, without regard to its conflict of law provisions.
          Any dispute not resolved informally shall be subject to binding
          arbitration in Austin, Texas, administered under the rules of the
          American Arbitration Association.
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          We may update these Terms from time to time. Material changes will be
          communicated via email or in-app notice at least 30 days before taking
          effect. Continued use of the Service after the effective date
          constitutes acceptance of the updated Terms.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 11 */}
      <Box id="contact" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
          11. Contact
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          For questions about these Terms, please contact us:
        </Typography>
        <Paper elevation={1} sx={{ p: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', mb: 3 }}>
          <Typography sx={{ ...legalBodyTextSx, fontWeight: 600, mb: 0.5 }}>
            Data Edge Services LLC
          </Typography>
          <Typography sx={{ ...legalBodyTextSx, mb: 0.5 }}>
            doing business as MyLojic
          </Typography>
          <Typography sx={legalBodyTextSx}>
            Email:{' '}
            <Link href="mailto:legal@mylojic.com" sx={{ color: 'primary.main' }}>
              legal@mylojic.com
            </Link>
          </Typography>
          <Typography sx={legalBodyTextSx}>
            Website: www.mylojic.com
          </Typography>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, bgcolor: 'primary.main', mt: 4 }}>
          <Typography sx={{ color: 'white', fontWeight: 600, textAlign: 'center' }}>
            BY USING MYLOJIC, YOU ACKNOWLEDGE THAT YOU HAVE READ AND AGREE TO
            THESE TERMS OF SERVICE.
          </Typography>
        </Paper>
      </Box>

    </Container>
  </LegalPageShell>
  );
};

export default TermsOfService;
